import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '@/api/client';
import { LoadingState } from '@/components/Spinner';
import { useAuthStore } from '@/state/authStore';
import { isClientUserRole } from '@/types/user';

interface Client {
  id: string; name: string; owner_coach_id: string; coach_name: string; feedbacker_count: number;
  information_disclosure_agreement_uploaded?: boolean;
  is_internal_operation: boolean;
}
interface Feedbacker { id: string; name: string; email: string }
type ClientOperationFilter = 'all' | 'coach' | 'internal';
type ClientAssignmentTab = 'own' | 'feedback';

const COMPANY_OVERVIEW_URL_MAX_LENGTH = 2048;

export function ClientListPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const user = useAuthStore(s => s.currentUser);
  const canCreateClient = !isClientUserRole(user?.role);
  const [clients, setClients] = useState<Client[]>([]);
  const canFilterByOperation = Boolean(
    user?.is_admin
      || (user?.is_feedbacker && clients.some(client => client.is_internal_operation)),
  );
  const [feedbackers, setFeedbackers] = useState<Feedbacker[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [newName, setNewName] = useState('');
  const [newCompanyUrl, setNewCompanyUrl] = useState('');
  const [urlError, setUrlError] = useState<string | null>(null);
  const [selectedFBs, setSelectedFBs] = useState<Set<string>>(new Set());
  const [creating, setCreating] = useState(false);

  const operationFilterParam = searchParams.get('operation');
  const operationFilter: ClientOperationFilter =
    operationFilterParam === 'coach' || operationFilterParam === 'internal'
      ? operationFilterParam
      : 'all';
  const assignmentTabParam = searchParams.get('assignment');

  const updateListSearchParam = (
    key: 'operation' | 'assignment',
    value: ClientOperationFilter | ClientAssignmentTab,
    defaultValue: ClientOperationFilter | ClientAssignmentTab,
  ) => {
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      if (value === defaultValue) next.delete(key);
      else next.set(key, value);
      return next;
    }, { replace: true });
  };

  const fetchClients = useCallback(() => {
    setLoading(true);
    api.get('/clients').then(r => { setClients(r.data); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  }, []);

  useEffect(() => { fetchClients(); }, [fetchClients]);

  const openForm = async () => {
    try {
      const resp = await api.get('/admin/feedbackers');
      setFeedbackers(resp.data);
    } catch {
      setFeedbackers([]);
    }
    setShowForm(true);
  };

  const toggleFB = (id: string) => {
    setSelectedFBs(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleCreate = async () => {
    if (!newName.trim()) return;
    const companyUrl = newCompanyUrl.trim();
    if (companyUrl.length > COMPANY_OVERVIEW_URL_MAX_LENGTH) {
      setUrlError(`URLは${COMPANY_OVERVIEW_URL_MAX_LENGTH}文字以内で入力してください`);
      return;
    }
    if (companyUrl && !/^https?:\/\//i.test(companyUrl)) {
      setUrlError('http:// または https:// で始まるURLを入力してください');
      return;
    }
    setUrlError(null);
    setCreating(true);
    try {
      await api.post('/clients', {
        name: newName.trim(),
        company_overview_url: companyUrl || undefined,
        feedbacker_ids: Array.from(selectedFBs),
      });
      setShowForm(false);
      setNewName('');
      setNewCompanyUrl('');
      setSelectedFBs(new Set());
      fetchClients();
    } catch (e: unknown) {
      const detail = (e as { response?: { data?: { detail?: string | { message?: string }; message?: string } } }).response?.data?.detail;
      const msg = typeof detail === 'string'
        ? detail
        : detail?.message ?? (e as { response?: { data?: { message?: string } } }).response?.data?.message;
      alert(msg || 'クライアントの追加に失敗しました');
    } finally {
      setCreating(false);
    }
  };

  if (loading) return <div className="p-8 flex justify-center"><LoadingState /></div>;
  if (error) return <div className="p-8 text-center text-red-500">{error}</div>;

  const visibleClients = !canFilterByOperation || operationFilter === 'all'
    ? clients
    : clients.filter(client => (
      operationFilter === 'internal' ? client.is_internal_operation : !client.is_internal_operation
    ));
  const shouldSplitByAssignment = user?.role === 'feedbacker' && !user.is_admin;
  const ownClients = shouldSplitByAssignment
    ? visibleClients.filter(client => String(client.owner_coach_id) === String(user.id))
    : [];
  const feedbackTargetClients = shouldSplitByAssignment
    ? visibleClients.filter(client => String(client.owner_coach_id) !== String(user.id))
    : [];
  const defaultAssignmentTab: ClientAssignmentTab = shouldSplitByAssignment
    && ownClients.length > 0
    ? 'own'
    : 'feedback';
  const activeAssignmentTab: ClientAssignmentTab =
    assignmentTabParam === 'own' || assignmentTabParam === 'feedback'
      ? assignmentTabParam
      : defaultAssignmentTab;
  const clientListSearch = searchParams.toString();

  const renderClientGrid = (items: Client[], emptyMessage: string) => (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {items.map(c => (
        <div
          key={c.id}
          onClick={() => navigate(`/clients/${c.id}`, { state: { clientListSearch } })}
          className="cursor-pointer rounded-lg bg-white p-5 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-start justify-between gap-2">
            <h3 className="text-lg font-semibold text-gray-900">{c.name}</h3>
            {canFilterByOperation && (
              <span className={`shrink-0 rounded px-2 py-0.5 text-xs font-medium ${
                c.is_internal_operation
                  ? 'bg-blue-100 text-blue-700'
                  : 'bg-gray-100 text-gray-600'
              }`}>
                {c.is_internal_operation ? 'クライアント内部運営' : 'コーチ'}
              </span>
            )}
          </div>
          <div className="mt-3 space-y-1 text-sm text-gray-600">
            <p>担当コーチ: {c.coach_name}</p>
            <p>フィードバッカー: {c.feedbacker_count} 名</p>
            <p>
              合意書PDF:{' '}
              <span className={c.information_disclosure_agreement_uploaded ? 'text-green-700' : 'text-red-600'}>
                {c.information_disclosure_agreement_uploaded ? 'アップロード済み' : '未アップロード'}
              </span>
            </p>
          </div>
        </div>
      ))}
      {items.length === 0 && <p className="text-gray-400">{emptyMessage}</p>}
    </div>
  );

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <h1 className="text-xl font-semibold text-gray-900">クライアント一覧</h1>
        {canCreateClient && (
          <button type="button" onClick={openForm} className="rounded bg-blue-600 px-4 py-2 text-sm text-white hover:bg-blue-700">+ クライアント追加</button>
        )}
      </div>

      {canFilterByOperation && (
        <div className="mb-4 flex items-center gap-2">
          <label htmlFor="client-operation-filter" className="text-sm text-gray-600">運用区分</label>
          <select
            id="client-operation-filter"
            value={operationFilter}
            onChange={event => updateListSearchParam(
              'operation',
              event.target.value as ClientOperationFilter,
              'all',
            )}
            className="rounded border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700"
          >
            <option value="all">すべて</option>
            <option value="coach">コーチ</option>
            <option value="internal">クライアント内部運営</option>
          </select>
        </div>
      )}

      {canCreateClient && showForm && (
        <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50 p-4">
          <h3 className="text-sm font-medium text-blue-900">新規クライアント追加</h3>
          <div className="mt-2 space-y-3">
            <div>
              <label className="block text-xs text-gray-600 mb-1">クライアント略称</label>
              <input type="text" placeholder="例: ケイタスプラン" value={newName}
                onChange={e => setNewName(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleCreate()}
                className="w-full rounded border border-gray-300 px-3 py-1.5 text-sm" />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">会社概要URL（任意）</label>
              <input type="url" placeholder="https://example.com/about" value={newCompanyUrl}
                onChange={e => {
                  setNewCompanyUrl(e.target.value);
                  if (urlError) setUrlError(null);
                }}
                onKeyDown={e => e.key === 'Enter' && handleCreate()}
                maxLength={COMPANY_OVERVIEW_URL_MAX_LENGTH}
                className="w-full rounded border border-gray-300 px-3 py-1.5 text-sm" />
              {urlError && (
                <p className="mt-1 text-xs text-red-600">{urlError}</p>
              )}
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">フィードバッカーを選択（複数可）</label>
              <div className="max-h-40 overflow-y-auto rounded border border-gray-200 bg-white p-2 space-y-1">
                {feedbackers.length === 0 ? (
                  <p className="text-xs text-gray-400">フィードバッカー権限を持つユーザーがいません</p>
                ) : feedbackers.map(fb => (
                  <label key={fb.id} className="flex items-center gap-2 rounded px-2 py-1 hover:bg-gray-50 cursor-pointer">
                    <input type="checkbox" checked={selectedFBs.has(fb.id)} onChange={() => toggleFB(fb.id)} />
                    <span className="text-sm">{fb.name}</span>
                    <span className="text-xs text-gray-400">{fb.email}</span>
                  </label>
                ))}
              </div>
              <p className="mt-1 text-xs text-gray-400">
                デフォルトフィードバッカー（あなたの直属上司）は自動で追加されます
              </p>
            </div>
            <div className="flex gap-2">
              <button type="button" onClick={handleCreate} disabled={creating || !newName.trim()}
                className="rounded bg-blue-600 px-4 py-1.5 text-sm text-white hover:bg-blue-700 disabled:opacity-50">
                {creating ? '追加中...' : '追加'}
              </button>
              <button type="button" onClick={() => { setShowForm(false); setNewName(''); setNewCompanyUrl(''); setUrlError(null); setSelectedFBs(new Set()); }}
                className="rounded border border-gray-300 px-3 py-1.5 text-sm text-gray-600">キャンセル</button>
            </div>
          </div>
        </div>
      )}

      {shouldSplitByAssignment ? (
        <>
          <div className="mb-4 border-b border-gray-200">
            <nav className="-mb-px flex gap-6" aria-label="クライアント表示切替">
              <button
                type="button"
                onClick={() => updateListSearchParam('assignment', 'feedback', defaultAssignmentTab)}
                className={`border-b-2 px-1 py-3 text-sm font-medium transition-colors ${
                  activeAssignmentTab === 'feedback'
                    ? 'border-blue-600 text-gray-900'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
                aria-current={activeAssignmentTab === 'feedback' ? 'page' : undefined}
              >
                フィードバック対象のクライアント（{feedbackTargetClients.length}件）
              </button>
              <button
                type="button"
                onClick={() => updateListSearchParam('assignment', 'own', defaultAssignmentTab)}
                className={`border-b-2 px-1 py-3 text-sm font-medium transition-colors ${
                  activeAssignmentTab === 'own'
                    ? 'border-blue-600 text-gray-900'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
                aria-current={activeAssignmentTab === 'own' ? 'page' : undefined}
              >
                自身が担当するクライアント（{ownClients.length}件）
              </button>
            </nav>
          </div>
          {activeAssignmentTab === 'own'
            ? renderClientGrid(ownClients, '自身が担当するクライアントはありません')
            : renderClientGrid(feedbackTargetClients, 'フィードバック対象のクライアントはありません')}
        </>
      ) : renderClientGrid(visibleClients, 'クライアントがありません')}
    </div>
  );
}
